import { useMemo } from 'react';
import { Trophy, Flame, Calendar, TrendingUp, Star, AlertTriangle } from 'lucide-react';

export default function PersonalRecords({ individualTrends }) {
  const records = useMemo(() => {
    if (!individualTrends || individualTrends.length < 3) return null;

    // Best / worst days
    const scored = individualTrends.map(d => ({
      ...d,
      overall: (d.mood || 0) + (d.energy || 0) + (d.confidence || 0) + (d.focus || 0) + (d.performance_rating || 0) - (d.stress || 0)
    }));
    const best = scored.reduce((a, b) => a.overall > b.overall ? a : b);
    const worst = scored.reduce((a, b) => a.overall < b.overall ? a : b);

    // Longest improving streak
    let maxStreak = 0, curStreak = 0;
    for (let i = 1; i < scored.length; i++) {
      if (scored[i].overall >= scored[i - 1].overall) { curStreak++; maxStreak = Math.max(maxStreak, curStreak); }
      else curStreak = 0;
    }

    // Check-in consistency
    const totalDays = Math.ceil((new Date(individualTrends[individualTrends.length - 1].date) - new Date(individualTrends[0].date)) / 86400000) + 1;
    const consistency = Math.round((individualTrends.length / Math.max(totalDays, 1)) * 100);

    // Best metric
    const metrics = ['mood', 'energy', 'confidence', 'focus', 'performance_rating'];
    const metricAvgs = metrics.map(m => ({
      key: m,
      avg: +(individualTrends.reduce((s, d) => s + (d[m] || 0), 0) / individualTrends.length).toFixed(1)
    }));
    const bestMetric = metricAvgs.reduce((a, b) => a.avg > b.avg ? a : b);
    const worstMetric = metricAvgs.reduce((a, b) => a.avg < b.avg ? a : b);
    const metricLabels = { mood: 'Mood', energy: 'Energy', confidence: 'Confidence', focus: 'Focus', performance_rating: 'Performance' };

    return { best, worst, maxStreak, consistency, totalDays, bestMetric: { ...bestMetric, label: metricLabels[bestMetric.key] }, worstMetric: { ...worstMetric, label: metricLabels[worstMetric.key] }, totalCheckins: individualTrends.length };
  }, [individualTrends]);

  if (!records) return null;

  const cards = [
    { icon: Trophy, label: 'Best Day', value: new Date(records.best.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), sub: `Score: ${records.best.overall.toFixed(0)}`, color: 'text-amber-600 bg-amber-50' },
    { icon: Flame, label: 'Longest Streak', value: `${records.maxStreak} days`, sub: 'Consecutive improvement', color: 'text-orange-600 bg-orange-50' },
    { icon: Calendar, label: 'Check-in Rate', value: `${records.consistency}%`, sub: `${records.totalCheckins} of ${records.totalDays} days`, color: 'text-blue-600 bg-blue-50' },
    { icon: Star, label: 'Strongest Area', value: records.bestMetric.label, sub: `Avg: ${records.bestMetric.avg}/10`, color: 'text-emerald-600 bg-emerald-50' },
    { icon: AlertTriangle, label: 'Growth Area', value: records.worstMetric.label, sub: `Avg: ${records.worstMetric.avg}/10`, color: 'text-red-500 bg-red-50' },
    { icon: TrendingUp, label: 'Toughest Day', value: new Date(records.worst.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), sub: `Score: ${records.worst.overall.toFixed(0)}`, color: 'text-gray-600 bg-gray-50' },
  ];

  return (
    <div data-testid="personal-records" className="bg-white rounded-xl shadow-lg p-6 border border-emerald-100">
      <h2 className="text-lg font-bold text-gray-900 mb-4">Personal Insights & Records</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="text-center p-3 rounded-lg border border-gray-100 hover:shadow-md transition-shadow">
              <div className={`inline-flex p-2 rounded-full mb-2 ${c.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <p className="text-[11px] text-gray-500 font-medium">{c.label}</p>
              <p className="text-sm font-bold text-gray-900 mt-0.5">{c.value}</p>
              <p className="text-[10px] text-gray-400">{c.sub}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
